/**
 * Утилиты для работы с HEIC/HEIF:
 * - браузер не показывает HEIC в <img>;
 * - сервер может не иметь Imagick для конвертации.
 * Конвертация в JPG/PNG выполняется в браузере (heic-to).
 */

export function isHeicFile(file: File): boolean {
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    /\.heic$/i.test(file.name) ||
    /\.heif$/i.test(file.name)
  )
}

export type ProcessFilesErrorHandler = (
  fileName: string,
  message: string
) => void

/**
 * Обрабатывает массив файлов: HEIC/HEIF конвертирует в JPG (или PNG при ошибке),
 * остальные оставляет как есть. Возвращает файлы и object URL для превью.
 */
export async function processFilesWithHeicConversion(
  files: File[],
  onError?: ProcessFilesErrorHandler
): Promise<{ files: File[]; urls: string[] }> {
  const filesToAdd: File[] = []
  const urlsToAdd: string[] = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (isHeicFile(file)) {
      try {
        const { heicTo } = await import('heic-to')
        let outBlob: Blob | null = null
        try {
          outBlob = await heicTo({
            blob: file,
            type: 'image/jpeg',
            quality: 0.92,
          })
        } catch {
          outBlob = await heicTo({
            blob: file,
            type: 'image/png',
            quality: 0.92,
          })
        }
        if (outBlob) {
          const ext = outBlob.type === 'image/png' ? '.png' : '.jpg'
          const outName = file.name
            .replace(/\.heic$/i, ext)
            .replace(/\.heif$/i, ext)
          const outFile = new File([outBlob], outName, { type: outBlob.type })
          filesToAdd.push(outFile)
          urlsToAdd.push(URL.createObjectURL(outBlob))
        } else {
          onError?.(
            file.name,
            'Не удалось конвертировать. Сохраните фото в JPG и загрузите снова.'
          )
        }
      } catch (err) {
        console.error('HEIC conversion failed:', err)
        onError?.(
          file.name,
          'HEIC не поддерживается в этом браузере. Сохраните фото в JPG и загрузите снова.'
        )
      }
    } else {
      filesToAdd.push(file)
      urlsToAdd.push(URL.createObjectURL(file))
    }
  }

  return { files: filesToAdd, urls: urlsToAdd }
}

/**
 * Для одного файла: если HEIC/HEIF — конвертирует в JPG/PNG и возвращает новый File,
 * иначе возвращает исходный файл. При ошибке конвертации выбрасывает.
 */
export async function convertFileIfHeic(file: File): Promise<File> {
  if (!isHeicFile(file)) return file

  const { heicTo } = await import('heic-to')
  let outBlob: Blob | null = null
  try {
    outBlob = await heicTo({
      blob: file,
      type: 'image/jpeg',
      quality: 0.92,
    })
  } catch {
    outBlob = await heicTo({
      blob: file,
      type: 'image/png',
      quality: 0.92,
    })
  }
  if (!outBlob) throw new Error('Не удалось конвертировать HEIC.')
  const ext = outBlob.type === 'image/png' ? '.png' : '.jpg'
  const outName = file.name.replace(/\.heic$/i, ext).replace(/\.heif$/i, ext)
  return new File([outBlob], outName, { type: outBlob.type })
}
